import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import Dialog from '@mui/material/Dialog'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import { router } from '@inertiajs/react'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface TaskResult {
  id: number
  summary: string
  instructions_preview: string | null
  status: string
  status_color: string
  status_label: string
  board: { id: number; name: string; slug: string }
  assignee: AgentRef | null
  requestor: AgentRef
  web_url: string
}

interface NoteResult {
  id: number
  title: string
  content_preview: string | null
  notebook: { id: number; name: string; slug: string }
  folder_name: string | null
  created_by: AgentRef
  web_url: string
}

interface SearchResults {
  tasks?: TaskResult[]
  notes?: NoteResult[]
}

type FlatResult =
  | { type: 'task'; data: TaskResult }
  | { type: 'note'; data: NoteResult }

interface Props {
  open: boolean
  onClose: () => void
}

export function SearchDialog({ open, onClose }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResults | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const resultsRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  const flatResults = useMemo<FlatResult[]>(() => {
    if (!results) return []
    return [
      ...(results.tasks ?? []).map((t): FlatResult => ({ type: 'task', data: t })),
      ...(results.notes ?? []).map((n): FlatResult => ({ type: 'note', data: n })),
    ]
  }, [results])

  const taskCount = results?.tasks?.length ?? 0

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults(null)
      setSelectedIndex(0)
      return
    }

    const timer = setTimeout(async () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setLoading(true)
      try {
        const response = await fetch(`/search?q=${encodeURIComponent(query.trim())}`, {
          headers: { 'Accept': 'application/json' },
          signal: controller.signal,
        })
        if (response.ok) {
          const data: SearchResults = await response.json()
          setResults(data)
          setSelectedIndex(0)
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Search failed:', error)
        }
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [query])

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery('')
      setResults(null)
      setSelectedIndex(0)
      setLoading(false)
      abortRef.current?.abort()
    }
  }, [open])

  const handleSelect = useCallback((result: FlatResult) => {
    onClose()
    router.visit(result.data.web_url)
  }, [onClose])

  // Scroll selected item into view
  useEffect(() => {
    if (!resultsRef.current) return
    const selected = resultsRef.current.querySelector('[data-selected="true"]')
    selected?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, flatResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (flatResults[selectedIndex]) {
        handleSelect(flatResults[selectedIndex])
      }
    }
  }

  const hasQuery = query.trim().length > 0
  const hasResults = flatResults.length > 0

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '70vh',
          overflow: 'hidden',
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.3)' } },
      }}
    >
      <Box sx={{ p: 2, pb: 0 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search tasks and notes..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="outlined"
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : null,
            },
          }}
        />
      </Box>

      <Box ref={resultsRef} sx={{ overflow: 'auto', px: 1, pb: 1, pt: 1 }}>
        {hasQuery && hasResults && (
          <>
            {(results?.tasks?.length ?? 0) > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Tasks
                </Typography>
                {results!.tasks!.map((task, idx) => (
                  <ResultRow
                    key={`task-${task.id}`}
                    selected={selectedIndex === idx}
                    onClick={() => handleSelect({ type: 'task', data: task })}
                  >
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: task.status_color, flexShrink: 0, mt: 0.5 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {task.summary}
                      </Typography>
                      {task.instructions_preview && (
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.8rem' }}>
                          {task.instructions_preview}
                        </Typography>
                      )}
                    </Box>
                    <Chip label={task.board.name} size="small" variant="outlined" sx={{ flexShrink: 0, fontSize: '0.7rem', height: 22 }} />
                  </ResultRow>
                ))}
              </Box>
            )}

            {(results?.notes?.length ?? 0) > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Notes
                </Typography>
                {results!.notes!.map((note, idx) => (
                  <ResultRow
                    key={`note-${note.id}`}
                    selected={selectedIndex === taskCount + idx}
                    onClick={() => handleSelect({ type: 'note', data: note })}
                  >
                    <ArticleOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0, mt: 0.25 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {note.title}
                      </Typography>
                      {note.content_preview && (
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.8rem' }}>
                          {note.content_preview}
                        </Typography>
                      )}
                    </Box>
                    <Chip label={note.notebook.name} size="small" variant="outlined" sx={{ flexShrink: 0, fontSize: '0.7rem', height: 22 }} />
                  </ResultRow>
                ))}
              </Box>
            )}
          </>
        )}

        {hasQuery && !loading && !hasResults && results !== null && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4, fontSize: '0.875rem' }}>
            No results found
          </Typography>
        )}

        {!hasQuery && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4, fontSize: '0.875rem' }}>
            Type to search across tasks and notes
          </Typography>
        )}
      </Box>
    </Dialog>
  )
}

function ResultRow({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Box
      data-selected={selected}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        cursor: 'pointer',
        backgroundColor: selected ? 'action.hover' : 'transparent',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      {children}
    </Box>
  )
}
